/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { PagedTable } from 'bold-ui'
import { usePagedTableProps } from 'components/table'
import { toDate } from 'date-fns'
import { useAcompanhamentoUltimosContatosQuery, useCidadaoQuery } from 'graphql/hooks.generated'
import { PageParams, Sort, TipoRegistroHistoricoClinico } from 'graphql/types.generated'
import { useState } from 'react'
import { GRUPO_CBO_ACOMP_PRE_NATAL } from 'types/NivelCbo'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { formatDate } from 'util/date/formatDate'
import {
  AcompanhamentoUltimosContatosModel,
  convertContatoToModel,
} from 'view/atendimentos/detail/folha-rosto/components/ultimos-contatos/converter-ultimosContatos'
import { DetalhesUltimosContatosButton } from 'view/atendimentos/detail/folha-rosto/components/ultimos-contatos/detalhes/DetalhesUltimosContatosButton'
import { ProfissionalListLabel } from 'view/atendimentos/detail/historico/components/accordion/detail/ProfissionalListLabel'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'

export interface ConsultasGestacaoCardProps {
  cidadaoId: ID
  atendimentoId: ID
  dataInicioGestacao: Date
  historicoPath: string
}

export function ConsultasGestacaoCard(props: Readonly<ConsultasGestacaoCardProps>) {
  const { cidadaoId, atendimentoId, dataInicioGestacao, historicoPath } = props
  const [tableState, setTableState] = useState<{ pageParams: PageParams }>({
    pageParams: {
      size: 8,
      fetchPageInfo: true,
    },
  })

  const { data: cidadaoData, loading: loadingCidadao } = useCidadaoQuery({
    fetchPolicy: 'cache-first',
    variables: { id: cidadaoId },
    skip: !cidadaoId,
  })

  const { data, loading: loadingUltimosContatos } = useAcompanhamentoUltimosContatosQuery({
    variables: {
      input: {
        cidadaoId,
        somenteMeusRegistros: false,
        sortDirection: Sort.DESC,
        tiposRegistro: [TipoRegistroHistoricoClinico.PRE_NATAL, TipoRegistroHistoricoClinico.ATENDIMENTO_ODONTOLOGICO],
        excludeEscutaOrPreAtendimentoFromAtendProf: atendimentoId,
        pageParams: tableState.pageParams,
        filtrosAvancados: {
          periodo: { startDate: dataInicioGestacao },
          cod2002Cbos: [...GRUPO_CBO_ACOMP_PRE_NATAL],
          exibirRegistrosInativos: false,
        },
      },
    },
    skip: !cidadaoId,
    context: { newRequest: true },
  })

  const hasConsultas = data?.historico?.content?.length > 0

  const tableProps = usePagedTableProps({
    result: data?.historico,
    loading: loadingCidadao || (loadingUltimosContatos && !data),
    onChange: setTableState,
  })

  const renderProfissionalColumn = (consulta: AcompanhamentoUltimosContatosModel) => (
    <ProfissionalListLabel
      nome={consulta.profissional?.nome}
      nomeSocial={consulta.profissional?.nomeSocial}
      nomeCivil={consulta.profissional?.nomeCivil}
      cbo={consulta.cbo?.nome}
      ineEquipe={consulta.equipe?.ine}
      nomeEquipe={consulta.equipe?.nome}
    />
  )

  const renderIgColumn = (consulta: AcompanhamentoUltimosContatosModel) => {
    const igConsulta = calculateIdadeGestacional(dataInicioGestacao, toDate(consulta.dataAtendimento))
    return `${formatIdadeGestacional(igConsulta.emDias)}`
  }

  const renderLupaColumn = (consulta: AcompanhamentoUltimosContatosModel) => (
    <DetalhesUltimosContatosButton
      disabled={!hasConsultas}
      contato={convertContatoToModel(consulta)}
      dataNascimentoCidadao={cidadaoData?.cidadao?.dataNascimento}
    />
  )

  return (
    <AcompanhamentoCard
      id='consultas'
      title='Consultas de pré-natal'
      loading={loadingCidadao || loadingUltimosContatos}
      placeholder='Nenhum registro encontrado'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar histórico' path={historicoPath} />}
      justifyHeaderContent='space-between'
      bodyPadding={hasConsultas ? '0' : undefined}
    >
      {hasConsultas && (
        <PagedTable
          columns={[
            {
              name: 'dataRealizacao',
              header: 'Data da realização',
              style: styles.content,
              render: (consulta: AcompanhamentoUltimosContatosModel) => formatDate(consulta.dataAtendimento),
            },
            {
              name: 'profissional',
              header: 'Profissional',
              style: styles.content,
              render: renderProfissionalColumn,
            },
            {
              name: 'igConsulta',
              header: 'IG na consulta',
              style: styles.content,
              render: renderIgColumn,
            },
            {
              name: 'visualizarInformacoes',
              header: '',
              style: styles.lupa,
              render: renderLupaColumn,
            },
          ]}
          {...tableProps}
          sizeOptions={[8]}
        />
      )}
    </AcompanhamentoCard>
  )
}

const styles = {
  content: css`
    padding: 0.75rem;
  `,
  lupa: css`
    padding: 0;
  `,
}
